import { App, Form, Input } from "antd";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CustomActionButtons,
  CustomInput,
  CustomModal,
  CustomSelect,
} from "sipli-fleet-core/components";
import {
  useMutationCreateVehicleType,
  useMutationUpdateVehicleType,
} from "src/queries/administrator/vehicleType.queries";
import type {
  VehicleTypePayload,
  VehicleTypeRecord,
} from "src/services/administrator/vehicleTypeService";

interface VehicleTypeFormModalProps {
  open: boolean;
  record?: VehicleTypeRecord | null;
  onClose: () => void;
}

interface VehicleTypeFormValues {
  name: string;
  active: string;
  description?: string;
}

const isActive = (value: unknown) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") return ["active", "true", "1"].includes(value.toLowerCase());
  return false;
};

const VehicleTypeFormModal = ({ open, record, onClose }: VehicleTypeFormModalProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<VehicleTypeFormValues>();
  const isEditing = Boolean(record?.id);
  const createMutation = useMutationCreateVehicleType();
  const updateMutation = useMutationUpdateVehicleType();

  const statusOptions = useMemo(
    () => [
      { label: t("active"), value: "true" },
      { label: t("inactive"), value: "false" },
    ],
    [t],
  );

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      name: record?.name ?? "",
      active: String(record ? isActive(record.active) : true),
      description: record?.description ?? "",
    });
  }, [form, open, record]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const payload: VehicleTypePayload = {
      name: values.name.trim(),
      active: values.active === "true",
      description: values.description?.trim() ?? "",
    };

    try {
      if (isEditing && record?.id) {
        await updateMutation.mutateAsync({ id: record.id, payload });
        message.success(t("vehicle_type_updated"));
      } else {
        await createMutation.mutateAsync(payload);
        message.success(t("vehicle_type_created"));
      }
      handleClose();
    } catch {
      message.error(t("setup_save_failed"));
    }
  };

  return (
    <CustomModal
      open={open}
      title={t(isEditing ? "edit_vehicle_type" : "add_vehicle_type")}
      onCancel={handleClose}
      destroyOnHidden
      width={560}
      footer={null}
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        className="grid grid-cols-1 pt-4 gap-x-4 sm:grid-cols-2"
      >
        <Form.Item
          name="name"
          label={t("vehicle_type_name")}
          rules={[
            { required: true, whitespace: true, message: t("name_required") },
            { max: 64, message: t("name_max_length", { count: 64 }) },
          ]}
          className="mb-3!"
        >
          <CustomInput placeholder={t("enter_name")} maxLength={64} />
        </Form.Item>

        <Form.Item
          name="active"
          label={t("status")}
          rules={[{ required: true, message: t("status_required") }]}
          className="mb-3!"
        >
          <CustomSelect options={statusOptions} placeholder={t("select")} />
        </Form.Item>

        <Form.Item
          name="description"
          label={t("description")}
          rules={[{ max: 255, message: t("description_too_long") }]}
          className="sm:col-span-2 mb-3!"
        >
          <Input.TextArea
            rows={4}
            placeholder={t("enter_description")}
            maxLength={255}
            className="px-2 py-4 custom-input no-resize"
          />
        </Form.Item>

        <Form.Item className="sm:col-span-2 mb-0!">
          <CustomActionButtons
            onCancel={handleClose}
            onOk={handleSubmit}
            loading={createMutation.isPending || updateMutation.isPending}
            okProps={{ htmlType: "button" }}
            cancelBtnLabel="cancel"
            okBtnLabel={isEditing ? "update" : "add"}
          />
        </Form.Item>
      </Form>
    </CustomModal>
  );
};

export default VehicleTypeFormModal;
